import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { ZagIcon } from '../icon/icon';

export type ChipColor = 'primary' | 'success' | 'warning' | 'error' | 'info' | 'neutral';

/**
 * Chip / tag pill. Supports optional icon (project with `[chipIcon]` attribute),
 * tone variants matching the badge palette, and a removable variant that shows
 * a close button and emits `remove` when clicked.
 *
 * @example
 * ```html
 * <!-- Basic -->
 * <zag-chip color="success">Active</zag-chip>
 *
 * <!-- With icon -->
 * <zag-chip color="info">
 *   <span zagIcon="person" chipIcon></span>
 *   HR Team
 * </zag-chip>
 *
 * <!-- Removable — provide a translated label describing what is removed for best accessibility -->
 * <zag-chip color="warning" [removable]="true" [removeLabel]="'common.remove' | translate" (remove)="onRemove()">Pending</zag-chip>
 * ```
 */
@Component({
  selector: 'zag-chip',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ZagIcon],
  template: `
    <ng-content select="[chipIcon]" />
    <span class="zag-chip__label"><ng-content /></span>
    @if (removable()) {
      <button type="button" class="zag-chip__remove" (click)="onRemove($event)" [attr.aria-label]="effectiveRemoveLabel()">
        <span zagIcon="close" class="zag-chip__remove-icon"></span>
      </button>
    }
  `,
  styleUrl: './chip.scss',
  host: {
    '[class.zag-chip]': 'true',
    '[class.zag-chip--primary]': 'color() === "primary"',
    '[class.zag-chip--success]': 'color() === "success"',
    '[class.zag-chip--warning]': 'color() === "warning"',
    '[class.zag-chip--error]': 'color() === "error"',
    '[class.zag-chip--info]': 'color() === "info"',
    '[class.zag-chip--neutral]': 'color() === "neutral"',
    '[class.zag-chip--removable]': 'removable()',
  },
})
export class Chip {
  private readonly translate = inject(TranslateService);
  private readonly _currentLang = toSignal(this.translate.onLangChange, {
    initialValue: { lang: this.translate.currentLang, translations: {} },
  });

  readonly color = input<ChipColor>('neutral');
  readonly removable = input<boolean>(false);
  /** Accessible label for the remove button. Defaults to the `common.remove` translation key. */
  readonly removeLabel = input<string>('');

  protected readonly effectiveRemoveLabel = computed(() => {
    this._currentLang();
    return this.removeLabel() || this.translate.instant('common.remove');
  });

  readonly remove = output<void>();

  protected onRemove(event: MouseEvent): void {
    event.stopPropagation();
    this.remove.emit();
  }
}
